import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Users, Crown, Heart, Sparkles } from 'lucide-react';
import CountUp from './CountUp';

/**
 * Homepage Rewards / Ambassador section
 *
 * Sits directly below the hero (after the slim DirectPayments trust strip).
 * Purpose: surface the customer/ambassador hook so the homepage delivers BOTH
 * emotional hooks (restaurant: lower fees / customer: rewards & community).
 *
 * Premium black + gold identity, asymmetric editorial layout, lifestyle image
 * reused from the /rewards landing for brand consistency. No arcade/gaming.
 */

const PERKS = [
  { icon: Gift, label: 'Earn Gift Credits', desc: 'Redeem on real food.' },
  { icon: Users, label: 'Invite Friends', desc: 'Unlimited referrals.' },
  { icon: Crown, label: 'VIP Ambassador Status', desc: 'Bronze · Gold · Elite.' },
  { icon: Heart, label: 'Support Local Restaurants', desc: 'Help them keep 100%.' },
];

const RewardsAmbassadorSection = () => {
  return (
    <section
      className="relative bg-black text-white overflow-hidden py-20 lg:py-28"
      data-testid="rewards-ambassador-section"
    >
      {/* Ambient gold glow */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-gold-500/[0.07] blur-[120px] pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 right-0 w-[600px] h-[600px] rounded-full bg-amber-500/[0.05] blur-[140px] pointer-events-none"
      />
      {/* Top hairline */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[88%] h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent"
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy + CTAs */}
          <div className="order-2 lg:order-1">
            {/* Eyebrow */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/[0.06] backdrop-blur-sm"
              data-testid="rewards-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5 text-gold-400" />
              <span className="text-[10.5px] font-bold tracking-[0.2em] uppercase text-gold-300">
                NoFee Ambassador Program
              </span>
            </div>

            {/* Headline */}
            <h2
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight"
              data-testid="rewards-headline"
            >
              Join The{' '}
              <span className="bg-gradient-to-b from-amber-200 via-gold-400 to-amber-500 bg-clip-text text-transparent">
                NoFee
              </span>
              <br />
              Ambassador Program.
            </h2>

            {/* Supporting */}
            <p
              className="mt-5 text-lg lg:text-xl text-white/75 leading-relaxed max-w-[560px]"
              data-testid="rewards-subheadline"
            >
              Help local restaurants grow.{' '}
              <span className="text-gold-300 font-semibold">
                Earn rewards along the way.
              </span>
            </p>

            <p className="mt-3 text-[15px] text-white/55 leading-relaxed max-w-[560px]">
              Share NoFee with friends, earn gift credits, unlock VIP ambassador
              status, and become part of a community keeping more money in
              your favorite local kitchens.
            </p>

            {/* Perks pills */}
            <ul
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-[600px]"
              data-testid="rewards-perks"
            >
              {PERKS.map(({ icon: Icon, label, desc }) => (
                <li
                  key={label}
                  className="group flex items-start gap-3 p-3.5 rounded-xl border border-white/8 bg-white/[0.025] hover:bg-white/[0.045] hover:border-gold-500/30 transition-all"
                  data-testid={`rewards-perk-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gold-500/10 border border-gold-500/25 flex items-center justify-center group-hover:bg-gold-500/15 transition-colors">
                    <Icon className="w-4 h-4 text-gold-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-white leading-tight">
                      {label}
                    </div>
                    <div className="text-[12px] text-white/50 mt-0.5">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/rewards"
                data-testid="rewards-cta-get-code"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black text-[15px] font-bold transition-all shadow-[0_12px_30px_-12px_rgba(212,175,55,0.7)]"
              >
                Get My Referral Code
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/rewards#how"
                data-testid="rewards-cta-learn-more"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/15 hover:border-gold-500/50 bg-white/[0.03] hover:bg-white/[0.06] text-white text-[15px] font-semibold transition-all"
              >
                Learn How It Works
              </Link>
            </div>

            {/* Tiny trust line */}
            <div
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/45"
              data-testid="rewards-trust"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-gold-500" />
                Free to join
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-gold-500" />
                Unlimited referrals
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-gold-500" />
                Gift credits — not cash games
              </span>
            </div>
          </div>

          {/* RIGHT — cinematic lifestyle */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/8 shadow-[0_50px_120px_-40px_rgba(0,0,0,0.9)]">
              <img
                src="/brand/ambassador_lifestyle.png"
                alt="NoFee Ambassador enjoying a meal from a local restaurant"
                className="w-full h-[420px] sm:h-[480px] lg:h-[560px] object-cover"
                loading="lazy"
                data-testid="rewards-section-image"
              />
              {/* Image vignette */}
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-tr from-black/55 via-black/10 to-transparent pointer-events-none"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 ring-1 ring-inset ring-gold-500/15 rounded-3xl pointer-events-none"
              />

              {/* Floating community badge */}
              <div
                className="absolute left-5 bottom-5 sm:left-6 sm:bottom-6 flex items-center gap-3 px-4 py-3 rounded-2xl bg-black/70 backdrop-blur-md border border-gold-500/30 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]"
                data-testid="rewards-community-badge"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-black" />
                </div>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-gold-300 font-bold">
                    Growing Ambassador Community
                  </div>
                  <div
                    className="text-[18px] sm:text-[20px] font-black text-white leading-none mt-0.5"
                    data-testid="rewards-community-count-wrap"
                  >
                    <CountUp
                      to={2500}
                      duration={2200}
                      suffix="+"
                      testid="rewards-community-count"
                      className="bg-gradient-to-b from-amber-200 via-gold-400 to-amber-500 bg-clip-text text-transparent"
                    />
                    <span className="ml-1.5 text-[12px] font-semibold text-white/65 tracking-normal">
                      ambassadors
                    </span>
                  </div>
                  <div className="text-[10.5px] text-white/45 mt-1">
                    Expanding daily across local communities
                  </div>
                </div>
              </div>

              {/* Floating gift credit chip */}
              <div
                className="hidden sm:flex absolute right-5 top-5 items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur-md border border-gold-500/30"
                data-testid="rewards-credit-chip"
              >
                <Gift className="w-3.5 h-3.5 text-gold-400" />
                <span className="text-[12px] font-semibold text-white">
                  +$5 gift credit earned
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom hairline */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[88%] h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent"
      />
    </section>
  );
};

export default RewardsAmbassadorSection;
